<template>
  <SearchForm
    :formModel="searchFormModel"
    :formStruct="searchFormStruct"
    :hasCreate="false /*$hasPermission(['approvalform_M'])*/"
    :submitButton="t('common.search')"
    :resetButton="t('common.reset')"
    @submit="submitSearch"
    @reset="resetForm"
  />
  <el-divider />
  <div>
    <el-dropdown
      v-if="$hasPermission(['ACC_PLAN_W'])"
      class="avatar-container right-menu-item hover-effect"
      trigger="click"
    >
      <div class="avatar-wrapper">
        <el-button
          type="primary"
          size="large"
          style="margin-bottom: 10px"
        >
          {{ t('Acceptance.orderFunction') }}
        </el-button>
      </div>
      <template #dropdown>
        <el-dropdown-menu>
          <el-dropdown-item
            @click="approvalProcessItemClick"
          >
            <span style="display:block;">{{ t('Acceptance.approvalProcess') }}</span>
          </el-dropdown-item>
          <el-dropdown-item
            divided
            @click="rejectApprovalItemClick"
          >
            <span style="display:block;">{{ t('Acceptance.rejectApproval') }}</span>
          </el-dropdown-item>
          <el-dropdown-item
            divided
            @click="handleBatchAcceptance"
          >
            <span style="display:block;">{{ t('Acceptance.batchAcceptance') }}</span>
          </el-dropdown-item>
          <el-dropdown-item
            divided
            @click="handleBatchReject"
          >
            <span style="display:block;">{{ t('Acceptance.batchReject') }}</span>
          </el-dropdown-item>
        </el-dropdown-menu>
      </template>
    </el-dropdown>
  </div>
  <SimpleTableForIcon
    ref="tableRef"
    :listLoading="listLoading"
    :tableData="tableData"
    :columnStruct="columnStruct"
    :hadSelect="true"
    :selectable="selectable"
    :operateColumnWidth="/*$hasPermission(['approvalform_M'])*/ true ? 240:80"
  >
    <template
      #default="slotProps"
    >
      <el-button
        @click="viewDetail(slotProps.row)"
        type="primary"
        size="mini"
      >
        {{ t('Acceptance.viewDetails') }}
      </el-button>
    </template>
  </SimpleTableForIcon>
  <el-pagination
    :total="total"
    v-show="total > 0"
    @size-change="handleSizeChange"
    @current-change="handleCurrentChange"
    :page-size="limit"
    :current-page="page"
    :page-sizes="[10, 20, 50, 100]"
    layout="total, sizes, prev, pager, next, jumper"
    style="text-align: right;"
  />
  <ApprovalProcessDialog
    v-if="approvalProcessDialogVisible"
    v-model="approvalProcessDialogVisible"
    :approvalData="selectedRows"
    :receiveType="receiveType"
    :companyCode="companyCode"
    :receive-bank-name="bankName"
    @refresh="onApprovalProcessDialogConfirm"
  />
  <AcceptanceReceiptDetail
    v-if="detailDialogVisible"
    v-model="detailDialogVisible"
    :approvalData="approvalData"
    @refresh="onApprovalProcessDialogConfirm"
  />
  <IndustrialBankApprovedDialog
    v-if="approvedDialogVisible"
    v-model="approvedDialogVisible"
    :receiveType="receiveType"
    :bankCode="bankCode"
    :bankName="bankName"
    :companyCode="companyCode"
    :reqDtRange="searchFormModel.reqDtRange"
    :draftNo="searchFormModel.draftNo"
    @refresh="onApprovalProcessDialogConfirm"
  />
</template>

<script lang="ts">
import { defineComponent, onMounted, reactive, ref, toRefs, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { createModel } from '@/utils/component'
import { formatDate, formatThousands, toStringMaximumTimeOfDay } from '@/utils'
import { getCIBReceiptPageList } from '@/apis/accepted-bill/acceptanceReceipt'
import ApprovalProcessDialog from '@/views/acceptance/acceptance-receipt/ApprovalProcessDialog.vue'
import AcceptanceReceiptDetail from '@/views/acceptance/acceptance-receipt/AcceptanceReceiptDetail.vue'
import { ElMessage } from 'element-plus'
import SearchForm from '@/components/form/search/SearchForm.vue'
import SimpleTableForIcon from '@/components/table/simple/SimpleTableForIcon.vue'
import {
  AcceptanceReceiptPageCIBParamModel, ReceiveAcceptanceHistoryModel
} from '@/model/accepted-bill/acceptedBillModel'
import { formatDraftType } from '@/utils/acceptanceBillFormatUtil'
import IndustrialBankApprovedDialog from '@/views/acceptance/acceptance-receipt/tabs/IndustrialBankApprovedDialog.vue'

export default defineComponent({
  name: 'IndustrialBankTab',
  components: {
    AcceptanceReceiptDetail,
    ApprovalProcessDialog,
    SearchForm,
    SimpleTableForIcon,
    IndustrialBankApprovedDialog
  },
  props: {
    isActive: {
      type: Boolean,
      required: true
    },
    bankCode: {
      type: String,
      required: true
    },
    bankName: {
      type: String,
      required: true
    },
    companyCode: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const { t } = useI18n()
    const tableRef = ref()
    const formatIconTooltip = (tip: string) => {
      if (tip.includes('-')) {
        const tipSplit = tip.split('-')
        const tipCode = tipSplit[0]
        const tipMessage = tipSplit.filter((t, i) => i > 0).join('')
        return `${tipCode}：${tipMessage}`
      }
      return tip
    }
    const statusOptions = [
      { label: t('acceptanceStatus.pendingConfirmation'), value: 0 },
      { label: t('acceptanceStatus.underReview'), value: 1 },
      { label: t('acceptanceStatus.pendingAction'), value: 2 },
      { label: t('acceptanceStatus.operated'), value: 3 },
      { label: t('acceptanceStatus.received'), value: 98 },
      { label: t('acceptanceStatus.rejected'), value: 99 }
    ]
    const searchStruct = [
      {
        prop: 'reqDtRange',
        label: t('Acceptance.requestTime'),
        component: 'el-date-picker',
        type: 'daterange',
        model: []
      },
      { prop: 'draftNo', label: t('Acceptance.draftNo'), component: 'el-input', type: 'text', model: '' }
    ]
    const acceptanceReceiptTableStruct = [
      { prop: 'index', label: t('Acceptance.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'requestObject', label: t('Acceptance.requestObject') },
      { prop: 'draftNo', label: t('Acceptance.draftNo'), width: 260 },
      {
        prop: 'requestDate',
        label: t('Acceptance.requestDate'),
        formatter: (row: ReceiveAcceptanceHistoryModel) => formatDate(row.requestDate, 'yyyy-MM-dd')
      },
      { prop: 'draftType', label: t('Acceptance.draftType'), width: 180, formatter: (row: ReceiveAcceptanceHistoryModel) => formatDraftType(row.draftType) },
      { prop: 'bankName', label: t('Acceptance.bankName'), width: 180 },
      { prop: 'draftAmt', label: t('Acceptance.draftAmt'), formatter: (row: any) => formatThousands(row.draftAmt), align: 'right' },
      {
        prop: 'status',
        label: t('Acceptance.status'),
        formatter: (row: ReceiveAcceptanceHistoryModel) => {
          return statusOptions.find(item => item.value === row.status)?.label
        },
        icon: (row: ReceiveAcceptanceHistoryModel) => {
          /**
           * status 支付单工作表状态，0：待确认，1: 审批中, 2: 待操作, 3: 已操作, 98: 签收, 99: 拒绝
           * receive_type 承兑接收类型，0：待确认，1: 签收, 2: 拒绝
           * submit_status 发起承兑接收/拒绝请求后状态，0：未完成，1：成功，2：失败
           * */
          if (row.status === 2 && row.receiveType !== 0 && row.submitStatus === 2) {
            return 'warning'
          } else {
            return ''
          }
        },
        showIcon: (row: any) => (row.status === 2 && row.receiveType !== 0 && row.submitStatus === 2),
        iconTooltip: 'submitComment',
        formatterIconTooltip: (tip: string) => formatIconTooltip(tip)
      }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as any,
      tableData: Array<ReceiveAcceptanceHistoryModel>(),
      columnStruct: acceptanceReceiptTableStruct,
      selectedRows: Array<ReceiveAcceptanceHistoryModel>(),
      approvalProcessDialogVisible: false,
      detailDialogVisible: false,
      approvedDialogVisible: false,
      approvalData: null as ReceiveAcceptanceHistoryModel | null,
      receiveType: null as number | null,
      listLoading: true,
      limit: 10,
      total: 0,
      page: 1,
      selectable(row: ReceiveAcceptanceHistoryModel) {
        return row.status === 0
      },
      handleCurrentChange(page?: any) {
        dataMap.getPageList(page)
      },
      handleSizeChange(val: any) {
        dataMap.getPageList(null, val)
      },
      async getPageList(page?: any, limit?: any) {
        if (page) {
          dataMap.page = page
        }
        if (limit) {
          dataMap.limit = limit
        }

        dataMap.listLoading = true
        let reqDtStart = null as string | null
        let reqDtEnd = null as string | null
        if (dataMap.searchFormModel.reqDtRange && dataMap.searchFormModel.reqDtRange.length > 1) {
          reqDtStart = dataMap.searchFormModel.reqDtRange[0] ? formatDate(dataMap.searchFormModel.reqDtRange[0]) : null
          reqDtEnd = dataMap.searchFormModel.reqDtRange[1] ? toStringMaximumTimeOfDay(dataMap.searchFormModel.reqDtRange[1]) : null
        }

        const params: AcceptanceReceiptPageCIBParamModel = {
          bankCode: props.bankCode,
          draftNo: dataMap.searchFormModel.draftNo,
          reqDtStart: reqDtStart,
          reqDtEnd: reqDtEnd,
          pageCurrent: dataMap.page,
          pageSize: dataMap.limit
        }
        const data = await getCIBReceiptPageList(params)
        if (data && data.data && data.data.list.length > 0) {
          dataMap.tableData = data.data.list
        } else {
          dataMap.tableData = []
          if (data?.code !== 200 && data?.msg) {
            ElMessage({
              message: data.msg,
              type: 'error',
              duration: 2000
            })
          }
        }
        dataMap.total = data?.data?.totalCount ?? 0
        tableRef.value.clearSelection()
        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getPageList()
      },
      resetForm() {
        console.log('reset')
      }
    })

    const approvalProcessItemClick = () => {
      if (tableRef.value && tableRef.value.getSelectionRows().length > 0) {
        dataMap.selectedRows = tableRef.value.getSelectionRows()
        dataMap.receiveType = 1
        dataMap.approvalProcessDialogVisible = true
      } else {
        ElMessage({
          message: t('Acceptance.selectApprovalTip'),
          type: 'error'
        })
      }
    }
    const rejectApprovalItemClick = () => {
      if (tableRef.value && tableRef.value.getSelectionRows().length > 0) {
        dataMap.selectedRows = tableRef.value.getSelectionRows()
        dataMap.receiveType = 2
        dataMap.approvalProcessDialogVisible = true
      } else {
        ElMessage({
          message: t('Acceptance.selectApprovalTip'),
          type: 'error'
        })
      }
    }
    const handleBatchAcceptance = () => {
      dataMap.receiveType = 1
      dataMap.approvedDialogVisible = true
    }
    const handleBatchReject = () => {
      dataMap.receiveType = 2
      dataMap.approvedDialogVisible = true
    }
    const viewDetail = async(row: ReceiveAcceptanceHistoryModel) => {
      dataMap.approvalData = row
      dataMap.detailDialogVisible = true
    }
    const onApprovalProcessDialogConfirm = async() => {
      await dataMap.getPageList(dataMap.page, dataMap.limit)
    }
    onMounted(() => {
      if (props.isActive) {
        const dateRage = []
        const currentDate = new Date()
        currentDate.setHours(0, 0, 0, 0)
        const startDate = new Date(currentDate)
        startDate.setMonth(startDate.getMonth() - 1)
        dateRage.push(startDate)
        dateRage.push(currentDate)
        dataMap.searchFormModel.reqDtRange = dateRage
        dataMap.getPageList(null, 10)
      }
    })
    watch(() => props.isActive, (isActive) => {
      if (isActive) {
        const dateRage = []
        const currentDate = new Date()
        currentDate.setHours(0, 0, 0, 0)
        const startDate = new Date(currentDate)
        startDate.setMonth(startDate.getMonth() - 1)
        dateRage.push(startDate)
        dateRage.push(currentDate)
        dataMap.searchFormModel.reqDtRange = dateRage
        dataMap.getPageList(null, 10)
      } else {
        dataMap.tableData = []
        tableRef.value.clearSelection()
      }
    })
    return {
      t,
      tableRef,
      approvalProcessItemClick,
      rejectApprovalItemClick,
      handleBatchAcceptance,
      handleBatchReject,
      viewDetail,
      onApprovalProcessDialogConfirm,
      ...toRefs(dataMap)
    }
  }
})
</script>

<style
  scoped
  lang="scss"
>

</style>
